"use client";

import React, { useEffect, useRef, useState } from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { ArrowRight, Building2, TrendingUp, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const partners = [{
  name: "GeoCore Materials",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_techflow_logo.png"
}, {
  name: "TerraScan Intl",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_innovatex_logo.png"
}, {
  name: "Precision Drills",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_globalmedia_logo.png"
}, {
  name: "SoilTech Systems",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_nexuscorp_logo.png"
}, {
  name: "Lithos Partners",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_vanguard_logo.png"
}, {
  name: "Apex Foundation",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569221/img/partner_synergy_logo.png"
}];
const chartData = [{
  month: "Jan",
  users: 1200,
  engagement: 800
}, {
  month: "Feb",
  users: 2100,
  engagement: 1200
}, {
  month: "Mar",
  users: 1800,
  engagement: 1500
}, {
  month: "Apr",
  users: 2800,
  engagement: 1900
}, {
  month: "May",
  users: 3200,
  engagement: 2400
}, {
  month: "Jun",
  users: 4500,
  engagement: 3100
}];
const chartConfig = {
  users: {
    label: "Project Reach",
    color: "var(--chart-1)",
    icon: Users
  },
  engagement: {
    label: "Field Analysis",
    color: "var(--chart-2)",
    icon: TrendingUp
  }
} satisfies ChartConfig;
export default function BusinessPartnership() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: chartRef,
    isVisible: isChartVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}